import { useDispatch, useSelector } from 'react-redux';
import { useKeycloak } from '@react-keycloak/web';
import { useHistory } from 'react-router';
import {
  Button,
  FormControl,
  Grid,
  Typography,
} from '@mui/material';
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, makeStyles } from '@material-ui/core';
import { Field, Form } from 'react-final-form';
import { SyntheticEvent, useCallback, useEffect, useState } from 'react';

import {
  selectUnidadeSelecionada,
  selectUnidadesDeNegocioAssociadas,
} from '../../../../features/usuario-logado/usuarioLogadoSlice';
import { setErrorFeedback, setSuccessFeedback } from '../../../../features/feedback/feedbackSlice';
import { GridItemModalProps } from '../../../common/components/cardsGrid/cardsModalGrid';
import { useUnidadeNegocioGuard } from '../../../common/hooks/useUnidadeNegocioGuard';
import { useListClientes } from '../../../common/hooks/useListClientes';
import MultiSelectStyled, { SimpleMultiSelectOption } from '../../../common/components/input/multiSelectStyled';
import QCXFinalDatePickerField from '../../../../shared-components/final-date-picker-field/QCXFinalDatePickerField';
import { generateAguardandoRegistroReport } from './AguardandoRegistroPage.helpers';
// + adicionar
import QCXSelectViaTransporteAutocomplete from
  '../../../../shared-components/select-via-transporte/QCXSelectViaTransporteAutocomplete';
import QCXSelectEmbarcacaoAutocomplete from
  '../../../../shared-components/select-embarcacao/QCXSelectEmbarcacaoAutocomplete';
import QCXSelectRecintoAduaneiroAutocomplete from
  '../../../../shared-components/select-recinto-aduaneiro/QCXSelectRecintoAduaneiroAutocomplete';
import { fetchByFilter } from '../../../../features/follow-up/followUpAPI';

interface ControlledValue {
  id: string | number;
  value: string;
}

const useStyles = makeStyles({
  root: {
    '& .MuiDialog-paper': {
      minWidth: '50vw',
      minHeight: '40vh',
    },
  },
});

const AguardandoRegistroPage = ({
  title,
  isOpen,
  closeModal,
}: GridItemModalProps) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const { keycloak } = useKeycloak();
  const { token } = keycloak;

  keycloak.updateToken(300);

  const selectedUnit = useSelector(selectUnidadeSelecionada);
  const tenantList = useSelector(selectUnidadesDeNegocioAssociadas);
  const [clientes] = useListClientes();

  const [selectedTenants, setSelectedTenants] = useState<SimpleMultiSelectOption[]>([]);
  const [selectedClients, setSelectedClients] = useState<SimpleMultiSelectOption[]>([]);
  const [businessError, setBusinessError] = useState(false);
  const [datas, setDatas] = useState({
    dataInicio: '',
    dataFim: '',
  });
  /* imediatamente após selectedClients */
  const [followUpList, setFollowUpList] = useState<ControlledValue[]>([]);
  const [selectedProcesses, setSelectedProcesses] = useState<ControlledValue[]>([]);
  const [isFetchingProcesses, setIsFetchingProcesses] = useState(false);

  const [selectedVia, setSelectedVia] = useState<any>(null);
  const [selectedEmbarcacao, setSelectedEmbarcacao] = useState<any>(null);
  const [selectedRecinto, setSelectedRecinto] = useState<any>(null);

  const [inspecao, setInspecao] = useState({ inicio: '', fim: '' });

  const fetchFilteredFollowUps = useCallback(async (clients: ControlledValue[]) => {
    try {
      setIsFetchingProcesses(true);
      const ids = clients.map(c => c.id).join(',');
      if (ids) {
        const resp = await fetchByFilter([
          { name: 'clientIds', value: ids },
          { name: 'incluirVinculadosDi', value: 'true' },
        ]);
        setFollowUpList(
          resp.data.map((f: any) => ({
            id: f.id?.toString() || '',
            value: f.numero?.toString() || '[Sem número]',
          }))
        );
      } else setFollowUpList([]);
    } finally {
      setIsFetchingProcesses(false);
    }
  }, []);


  useUnidadeNegocioGuard();

  const handleTenantsChange = (
    _e: any,
    value: SimpleMultiSelectOption[] | null
  ) => setSelectedTenants(value || []);

  const handleClientsChange = (
    event: SyntheticEvent<Element, Event>,
    value: { id: string | number | undefined; value: string | undefined }[] | null
  ) => {
    const newSelectedClients: ControlledValue[] = [];
    value?.forEach((val) => {
      const foundClient = clientes.find((c) => c.id === val.id);
      if (foundClient && foundClient.id) {
        if (!newSelectedClients.some((cli) => cli.id === foundClient.id)) {
          newSelectedClients.push({
            id: foundClient.id.toString(),
            value: `(${foundClient.pessoa?.cnpj}) ${foundClient.pessoa?.nome}`,
          });
        }
      }
    });
    setSelectedClients(newSelectedClients);
    setSelectedProcesses([]);
    fetchFilteredFollowUps(newSelectedClients);
  };

  const handleProcessesChange = (
    event: SyntheticEvent<Element, Event>,
    value: { id: string | number | undefined; value: string | undefined }[] | null
  ) => {
    const newSelectedProcesses: ControlledValue[] = [];
    value?.forEach((val) => {
      const foundProcess = followUpList.find((f) => f.id === val.id);
      if (foundProcess) {
        newSelectedProcesses.push(foundProcess);
      }
    });
    setSelectedProcesses(newSelectedProcesses);
  };

  const handleChangeDataInicio = (iso: string) =>
    setDatas((p) => ({ ...p, dataInicio: iso.split('T')[0] }));

  const handleChangeDataFim = (iso: string) =>
    setDatas((p) => ({ ...p, dataFim: iso.split('T')[0] }));

  const validarDatas = () =>
    datas.dataInicio !== '' &&
    datas.dataFim !== '' &&
    new Date(datas.dataInicio) <= new Date(datas.dataFim);

  const handleSubmit = async () => {
    keycloak.updateToken(300);

    if (!validarDatas()) {
      dispatch(setErrorFeedback({ message: 'Preencha as datas corretamente.' }));
      return;
    }

    if (selectedTenants.length === 0) {
      setBusinessError(true);
      dispatch(setErrorFeedback({ message: 'Selecione pelo menos uma Unidade.' }));
      return;
    }
    setBusinessError(false);

    if (token && selectedUnit) {

      const erro = await generateAguardandoRegistroReport(
        token,
        selectedUnit,
        selectedTenants.map(t => t.id.toString()),
        selectedClients.map(c => c.id.toString()),
        selectedProcesses.map(p => p.id.toString()),   // processosId
        selectedVia?.id?.toString() || null,           // viaId
        selectedEmbarcacao?.id?.toString() || null,    // embarcacaoId
        selectedRecinto?.id?.toString() || null,       // recintoId
        datas.dataInicio,
        datas.dataFim,
        inspecao.inicio,
        inspecao.fim
      );

      if (erro == null) {
        dispatch(setSuccessFeedback({ message: 'Relatório gerado com sucesso!' }));
      } else {
        dispatch(setErrorFeedback({ message: erro }));
      }
    }

    history.push('/importacao/relatorios');
  };

  return (
    <Dialog className={classes.root} open={isOpen} onClose={closeModal}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Form onSubmit={() => { }}>
          {() => (
            <form>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth error={businessError}>
                    <MultiSelectStyled
                      controlledValues={selectedTenants}
                      options={tenantList.map((t: { id: { toString: () => any; }; pessoa: { nomeResumido: any; }; }) => ({
                        id: t.id.toString(),
                        value: t.pessoa.nomeResumido,
                      }))}
                      onChangeAction={handleTenantsChange}
                      label="Unidade de Negócio"
                    />
                    {selectedTenants.length === 0 && (
                      <Typography variant="body2" color="error">
                        Este campo é obrigatório
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <MultiSelectStyled
                      controlledValues={selectedClients}
                      options={clientes.map((c) => ({
                        id: c.id ?? '',
                        value: `(${c.pessoa!.cnpj}) ${c.pessoa!.nome}`,
                      }))}
                      onChangeAction={handleClientsChange}
                      label="Clientes"
                    />
                  </FormControl>
                </Grid>

                {/* Processos */}
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <MultiSelectStyled
                      controlledValues={selectedProcesses}
                      options={followUpList}
                      onChangeAction={(_, v) => setSelectedProcesses(v || [])}
                      label="Processos"
                      disabled={selectedClients.length === 0 || isFetchingProcesses}
                      placeholder={
                        selectedClients.length === 0
                          ? 'Selecione um Cliente'
                          : isFetchingProcesses
                            ? 'Carregando...'
                            : 'Selecionar Processos'
                      }
                    />
                  </FormControl>
                </Grid>

                {/* Via Transporte */}
                <Grid item xs={12} sm={6}>
                  <QCXSelectViaTransporteAutocomplete
                    label="Via Transporte"
                    name="via"
                    initialValues={selectedVia}
                    onChange={(_: any, v: any) => setSelectedVia(v)}
                  />
                </Grid>

                {/* Embarcação */}
                <Grid item xs={12} sm={6}>
                  <QCXSelectEmbarcacaoAutocomplete
                    label="Embarcação"
                    name="embarcacao"
                    hideCode
                    initialValues={selectedEmbarcacao}
                    onChange={(_: any, v: any) => setSelectedEmbarcacao(v)}
                  />
                </Grid>

                {/* Armazém Despacho */}
                <Grid item xs={12} sm={6}>
                  <QCXSelectRecintoAduaneiroAutocomplete
                    label="Armazém Despacho"
                    name="recinto"
                    initialValues={selectedRecinto}
                    onChange={(_: any, v: any) => setSelectedRecinto(v)} staticValues={undefined} autoLoad={undefined} />
                </Grid>

                {/* Período Inspeção Madeira */}
                <Grid item xs={12} sm={6}>
                  <Field
                    name="inspecaoInicio"
                    render={({ input }) => (
                      <QCXFinalDatePickerField
                        id="aguardando-Registro-inspecao-inicio"
                        name={input.name}
                        label="Inspeção Madeira Início"
                        format="DD/MM/YYYY"
                        placeholder="DD/MM/YYYY"
                        required={false}
                        onChange={(d: any) => {
                          setInspecao((p) => ({ ...p, inicio: d.toISOString() }));
                          input.onChange(d);
                        }}
                        validate={undefined}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field
                    name="inspecaoFim"
                    render={({ input }) => (
                      <QCXFinalDatePickerField
                        id="aguardando-Registro-inspecao-fim"
                        name={input.name}
                        label="Inspeção Madeira Fim"
                        format="DD/MM/YYYY"
                        placeholder="DD/MM/YYYY"
                        required={false}
                        onChange={(d: any) => {
                          setInspecao((p) => ({ ...p, fim: d.toISOString() }));
                          input.onChange(d);
                        }}
                        validate={undefined}
                      />
                    )}
                  />
                </Grid>


                <Grid item xs={6}>
                  <Field
                    name="startDate"
                    render={({ input }) => (
                      <QCXFinalDatePickerField
                        id="aguardando-Registro-start"
                        name={input.name}
                        label="Previsão Registro Início"
                        required
                        format="DD/MM/YYYY"
                        placeholder="DD/MM/YYYY"
                        onChange={(d: any) => {
                          handleChangeDataInicio(d.toISOString());
                          input.onChange(d);
                        }}
                        validate={undefined}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={6}>
                  <Field
                    name="endDate"
                    render={({ input }) => (
                      <QCXFinalDatePickerField
                        id="aguardando-Registro-end"
                        name={input.name}
                        label="Previsão Registro Final"
                        required
                        format="DD/MM/YYYY"
                        placeholder="DD/MM/YYYY"
                        onChange={(d: any) => {
                          handleChangeDataFim(d.toISOString());
                          input.onChange(d);
                        }}
                        validate={undefined}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </form>
          )}
        </Form>
      </DialogContent>
      <DialogActions>
        <Box padding={2} display="flex" gridGap={4}>
          <Button variant="outlined" color="primary" onClick={closeModal}>
            Fechar
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleSubmit}
            fullWidth
            disabled={
              datas.dataFim === '' ||
              datas.dataInicio === '' ||
              selectedTenants.length === 0
            }
          >
            Gerar Relatório
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default AguardandoRegistroPage;
